import React from 'react';
import { Icons } from 'components/Icons';
import { DiagramPlatform, DiagramInfra, DiagramLabs, DiagramOntology } from './StrengthsDiagrams';

/**
 * Single active tab panel for the investors strengths section.
 */
export function StrengthsTabPanels({ t, besperAiLabUrl, strengthsTab }) {
  if (strengthsTab === 'platform') {
    return (
      <div className="investors-strengths-content-wrap investors-strengths-wrap-platform">
        <div className="investors-strengths-content">
          <h3 className="investors-strengths-content-title">{t('strengths.platform.title', 'B-esper Platform (Besper UG)')}</h3>
          <p className="investors-strengths-lead">{t('strengths.platform.lead', 'Besper UG operates the B-esper Platform: the Europe-first operating environment where companies run Kubernetes workloads, attach operational data services, and transact—marketplace, identity, billing, and compliance hooks in one product stack.')}</p>
          <p className="investors-strengths-lead investors-strengths-lead2">{t('strengths.platform.lead2', 'B-esper Bot is our flagship SME-focused chat SKU on that stack; the same rails extend to enterprise standardization and migration over time.')}</p>
          <ul className="investors-strengths-list">
            <li>{t('strengths.platform.adv1', 'Orchestration: managed Kubernetes and control plane for multi-cloud and regional placement.')}</li>
            <li>{t('strengths.platform.adv2', 'Marketplace & payments: European B2B discovery, integrated billing, and partner routes to market.')}</li>
            <li>{t('strengths.platform.adv3', 'Full stack: identity, compliance hooks, and platform services designed as one product.')}</li>
            <li>{t('strengths.platform.adv4', 'Partner model: structure to co-create offerings and sub-brands where it accelerates adoption.')}</li>
            <li>{t('strengths.platform.adv5', 'Roadmap: expand supported cloud primitives so larger architectures can run on B-esper over time.')}</li>
            <li>{t('strengths.platform.adv6', 'European posture: data residency, transparency, and alignment with EU digital sovereignty goals.')}</li>
          </ul>
        </div>
        <div className="investors-strengths-hero investors-strengths-hero--platform-logo-only" aria-hidden="true">
          <div className="investors-strengths-hero-diagram investors-strengths-platform-hero-diagram">
            <DiagramPlatform />
          </div>
        </div>
      </div>
    );
  }

  if (strengthsTab === 'infra') {
    return (
      <div className="investors-strengths-content-wrap investors-strengths-wrap-infra">
        <div className="investors-strengths-content">
          <h3 className="investors-strengths-content-title">{t('strengths.infra.title', 'B-esper Infra — compute')}</h3>
          <p className="investors-strengths-lead">{t('strengths.infra.lead', 'B-esper Infra is our compute infrastructure layer: capacity, scheduling, and operations for workloads that power the platform and selected customer pipelines—not a generic public hosting brand.')}</p>
          <p className="investors-strengths-lead investors-strengths-lead2">{t('strengths.infra.lead2', 'It exists so we can offer predictable performance, cost discipline, and governance over the metal and clusters behind B-esper.')}</p>
          <ul className="investors-strengths-list">
            <li>{t('strengths.infra.adv1', 'Dedicated compute pools for platform services and approved high-throughput workloads.')}</li>
            <li>{t('strengths.infra.adv2', 'Cluster operations: upgrades, isolation patterns, and capacity planning under our control.')}</li>
            <li>{t('strengths.infra.adv3', 'Cost and utilization visibility aligned with customer and internal FinOps needs.')}</li>
            <li>{t('strengths.infra.adv4', 'Security and compliance hooks at the infrastructure boundary (networking, access, logging).')}</li>
            <li>{t('strengths.infra.adv5', 'European footprint first; expansion follows product and regulatory requirements.')}</li>
            <li>{t('strengths.infra.adv6', 'Clear separation from the product platform: Infra supplies compute; the Platform supplies the product experience.')}</li>
          </ul>
        </div>
        <div className="investors-strengths-hero investors-strengths-hero--infra-logo-only" aria-hidden="true">
          <div className="investors-strengths-hero-diagram investors-strengths-infra-hero-diagram">
            <DiagramInfra />
          </div>
        </div>
      </div>
    );
  }

  if (strengthsTab === 'labs') {
    return (
      <div className="investors-strengths-content-wrap investors-strengths-wrap-labs">
        <div className="investors-strengths-content investors-strengths-ai">
          <div className="investors-strengths-ai-promo-head">
            <p className="investors-strengths-ai-desc investors-strengths-labs-desc">
              {t('aiLab.promoDescription', 'Deontic logic, embedded certificates, and auditable AI—built for infrastructure and European digital sovereignty.')}
            </p>
          </div>
          <h4 className="investors-strengths-ai-subtitle investors-strengths-labs-subtitle">{t('aiLab.title', 'B-esper AI Lab & Our Three Core Innovations')}</h4>
          <p className="investors-strengths-ai-copy investors-strengths-labs-copy">
            {t('aiLab.intro', 'Our research focuses on AI for infrastructure—enabling scalable compliance, reliable reasoning, and energy-efficient deployment. We build trustworthy, auditable AI rather than pursuing general artificial intelligence.')}
          </p>
          <p className="investors-strengths-ai-copy investors-strengths-labs-copy investors-strengths-labs-economy">
            {t(
              'aiLab.economyLead',
              'Beyond infrastructure AI, Labs explores a composable data–model exchange: versioned contributions, measurable impact in training, and downstream settlement ideas that keep creators and data providers aligned as models ship to inference.',
            )}
          </p>
          <div className="investors-pillars">
            <div className="investors-pillar-card">
              <div className="investors-pillar-icon" aria-hidden="true">
                <Icons.Document size={28} />
              </div>
              <h3>{t('aiLab.pillar1.title', 'Deontic Logic')}</h3>
              <p>{t('aiLab.pillar1.description', 'Formal logic for obligations, permissions, and prohibitions built into AI models from the ground up.')}</p>
            </div>
            <div className="investors-pillar-card">
              <div className="investors-pillar-icon" aria-hidden="true">
                <Icons.Security size={28} />
              </div>
              <h3>{t('aiLab.pillar2.title', 'Embedded Certificates')}</h3>
              <p>{t('aiLab.pillar2.description', 'Certificates embedded in training for verifiable AI behavior and provable compliance with ethical and legal constraints.')}</p>
            </div>
            <div className="investors-pillar-card">
              <div className="investors-pillar-icon" aria-hidden="true">
                <Icons.Chart size={28} />
              </div>
              <h3>{t('aiLab.pillar3.title', 'Auditable & Reliable AI')}</h3>
              <p>{t('aiLab.pillar3.description', 'Complete transparency and traceability in AI decision-making; AI systems that can be trusted to follow specified rules.')}</p>
            </div>
          </div>
          <p className="investors-for-infra investors-strengths-labs-for-infra">
            {t('aiLab.forInfrastructure', 'We push AI to be used in infrastructure: compliance verification, reliable reasoning at scale, and energy-efficient deployment—including at the edge.')}
          </p>
          <p className="investors-strengths-ai-high-risk investors-strengths-labs-high-risk">
            {t('aiLab.highRiskIndustries', 'We aim to enable the use of AI in infrastructure and high-risk industries—such as legal and medicine—based on our deontic knowledge graphs and query language, as well as deontically trained LLM systems.')}
          </p>
          <p className="investors-cta">
            {besperAiLabUrl ? (
              <a href={besperAiLabUrl} className="investors-strengths-ai-cta btn-ailab investors-strengths-labs-cta" target="_blank" rel="noopener noreferrer">
                {t('aiLab.cta', 'Explore B-esper AI Lab')}
                <Icons.ExternalLink size={18} />
              </a>
            ) : (
              <span className="investors-strengths-ai-cta btn-ailab investors-strengths-labs-cta">{t('aiLab.cta', 'Explore B-esper AI Lab')}</span>
            )}
          </p>
        </div>
        <div className="investors-strengths-hero investors-strengths-hero--labs-logo-only" aria-hidden="true">
          <div className="investors-strengths-hero-diagram investors-strengths-labs-hero-diagram">
            <DiagramLabs />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="investors-strengths-content-wrap investors-strengths-wrap-ontology">
      <div className="investors-strengths-content">
        <h3 className="investors-strengths-content-title">{t('strengths.ontology.title', 'B-esper Ontology')}</h3>
        <p className="investors-strengths-lead">{t('strengths.ontology.lead', 'B-esper Ontology sits on the B-esper Platform and unifies datasets and services—on Besper and connected external systems—into decision-grade analytics and workflows teams can defend in audit and board conversations.')}</p>
        <p className="investors-strengths-lead investors-strengths-lead2">{t('strengths.ontology.lead2', 'We aim to bring the depth of integrated intelligence historically affordable only at the largest vendors within reach of European SMEs and mid-market operators—without trading away governance or explainability.')}</p>
        <ul className="investors-strengths-list">
          <li>{t('strengths.ontology.adv1', 'Semantic layer: ontologies and knowledge graphs that align teams on “one meaning” for critical entities.')}</li>
          <li>{t('strengths.ontology.adv2', 'Deep analytics: multi-hop reasoning, cohort and trend analysis, and operational KPIs tied to lineage.')}</li>
          <li>{t('strengths.ontology.adv3', 'Actionable insight: alerts, recommendations, and workflow hooks grounded in auditable logic.')}</li>
          <li>{t('strengths.ontology.adv4', 'Governance: access control, retention, and export patterns suitable for regulated environments.')}</li>
          <li>{t('strengths.ontology.adv5', 'Platform-native: leverages B-esper identity, data, and compliance primitives where customers already run.')}</li>
          <li>{t('strengths.ontology.adv6', 'Ownership: majority-held by Besper Holding GmbH alongside other B-esper group companies.')}</li>
        </ul>
      </div>
      <div className="investors-strengths-hero investors-strengths-hero--ontology-logo-only" aria-hidden="true">
        <div className="investors-strengths-hero-diagram investors-strengths-ontology-hero-diagram">
          <DiagramOntology />
        </div>
      </div>
    </div>
  );
}
